import React, { useEffect, useContext, useState, useMemo } from "react";
import { ParticipantSearch } from "../components/Search/ParticipantSearch";
import Loader from "../components/UI/Loader";
import { useNavigate } from "react-router-dom";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import { MeContext, OrganizationContext } from "../App";
import SearchResultsTable from "../components/Search/SearchResultsTable";
import Protect from "../components/Layout/Protect";
import SideMenu from "../components/UI/SideMenu";
import Modal from "../components/UI/Modal";
import Button from "../components/UI/Button";
import { getAllPossibleDuplicateClients } from "../services/clientsService";
import { getPublicSubmissionsSummary } from "../services/formsService";
import { participantDataImportEnabled, programEnrollmentImportEnabled, publicFormsEnabled, publicSubmissionFollowUpsEnabled } from "../utils/features";
import { getLandingPageOptions } from "../utils/sideMenuHelpers";
import { FREE_PLAN_TYPE_ID } from "../constants/planTypes";
import { featuredLandingHelpGuideTips, LandingHelpGuideTip, landingHelpGuideTips } from "../data/helpGuides";
import { getActionablePublicSubmissionCount } from "../utils/publicSubmissionFeature";
import './LandingPage.css';

const LANDING_GUIDE_COUNT = 6;
const TIPS_PER_PAGE = 2;

const getTipWeight = (tip: LandingHelpGuideTip) => Math.max(tip.weight ?? 1, 0);

const selectWeightedTips = (tips: LandingHelpGuideTip[], count: number) => {
    const remaining = [...tips];
    const selected: LandingHelpGuideTip[] = [];

    while (selected.length < count && remaining.length > 0) {
        const totalWeight = remaining.reduce((total, tip) => total + getTipWeight(tip), 0);

        if (totalWeight <= 0) {
            selected.push(...remaining.slice(0, count - selected.length));
            break;
        }

        let marker = Math.random() * totalWeight;
        const selectedIndex = remaining.findIndex(tip => {
            marker -= getTipWeight(tip);
            return marker < 0;
        });

        const indexToUse = selectedIndex === -1 ? remaining.length - 1 : selectedIndex;
        selected.push(...remaining.splice(indexToUse, 1));
    }

    return selected;
};

const LandingPageV2: React.FC = () => {
    const me = useContext(MeContext);
    const organization = useContext(OrganizationContext);
    const navigate = useNavigate();
    const [apiError, setApiError] = useState<any>();
    const [totalDuplicates, setTotalDuplicates] = useState<string>();
    const [totalPublicSubmissions, setTotalPublicSubmissions] = useState<string>();
    const [currentPage, setCurrentPage] = useState(0);
    const [showPublicFormsPaywall, setShowPublicFormsPaywall] = useState(false);

    const isFreePlan = organization.value?.planTypeId === FREE_PLAN_TYPE_ID;

    // Select random tips on mount, allowing featured guides to appear more often.
    const shuffledTips = useMemo(() => {
        return selectWeightedTips(landingHelpGuideTips, LANDING_GUIDE_COUNT);
    }, []);

    const totalPages = Math.ceil(shuffledTips.length / TIPS_PER_PAGE);
    const currentTips = shuffledTips.slice(currentPage * TIPS_PER_PAGE, currentPage * TIPS_PER_PAGE + TIPS_PER_PAGE);

    const handlePrevPage = () => {
        setCurrentPage(prev => (prev === 0 ? totalPages - 1 : prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => (prev === totalPages - 1 ? 0 : prev + 1));
    };

    useEffect(() => {
        document.title = "Participant Search"
    }, []);

    useEffect(() => {
        getAllPossibleDuplicateClients().then(r => {
            setTotalDuplicates(r.length.toString())
        }).catch(e => {
            console.error(e);
            setApiError(e);
        });
    }, []);

    useEffect(() => {
        if (organization.loading || !organization.value || isFreePlan) {
            return;
        }

        getPublicSubmissionsSummary('').then(r => {
            setTotalPublicSubmissions(
                getActionablePublicSubmissionCount(r.results, publicSubmissionFollowUpsEnabled).toString()
            );
        }).catch(e => {
            console.error(e);
            setApiError(e);
        });
    }, [organization.loading, organization.value, isFreePlan]);

    useEffect(() => {
        if (organization.loading || me.loading) {
            return;
        }

        if (!organization.value || !me.user) {
            console.error('No organization or user found');
            return;
        }

        if (!organization.value?.tosAnswerOn && me.user?.id === organization.value?.createdBy) {
            navigate('/admin/tos');
        }
    }, [organization, me]);

    useEffect(() => {
        try {
            // trying to keep localStorage cleaned up
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('mergeParticipants_')) {
                    const valueItem = localStorage.getItem(key);
                    if (valueItem) {
                        const value = JSON.parse(valueItem) as { expiresOn?: number }
                        // clear out any mergeParticipants that are expired or have no expiration
                        if (!value?.expiresOn || value.expiresOn <= new Date().getTime()) {
                            localStorage.removeItem(key); // clearing out any remaining merge participants
                        }
                    }

                }
            }
        } catch { }
    }, []);

    if (organization.loading || me.loading) {
        return <Loader />
    }

    const sideMenuOptions = getLandingPageOptions(
        totalDuplicates,
        totalPublicSubmissions,
        publicFormsEnabled,
        isFreePlan,
        () => setShowPublicFormsPaywall(true),
        participantDataImportEnabled || programEnrollmentImportEnabled
    );

    const getHeaderContent = () => {
        return (
            <>
                <div className="landing-welcome">
                    <div className="landing-welcome-header">
                        <div>
                            <div className="landing-welcome-title">
                                Welcome back{me.user?.firstName ? `, ${me.user.firstName}` : ''}!
                            </div>
                            <div className="landing-welcome-subtitle">
                                Here are some tips to help you get the most out of countbubble
                            </div>
                        </div>
                        <a className="landing-help-link" href="/help">View all guides</a>
                    </div>
                </div>

                {featuredLandingHelpGuideTips.map(tip => (
                    <aside className="featured-guide" key={tip.linkUrl} aria-label="Featured guide">
                        <div className="featured-guide-content">
                            <span className="featured-guide-label">{tip.category}</span>
                            <div className="featured-guide-title">{tip.title}</div>
                            <div className="featured-guide-description">{tip.description}</div>
                        </div>
                        <a
                            href={tip.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="featured-guide-link"
                        >
                            {tip.linkText}
                        </a>
                    </aside>
                ))}

                {/* Tip Carousel */}
                <div className="tip-carousel">
                    <div className="tip-carousel-inner">
                        {currentTips.map((tip, index) => (
                            <div className="tip-card" key={index}>
                                <div className="tip-card-header">
                                    <span className="tip-card-category">{tip.category}</span>
                                </div>
                                <div className="tip-card-title">{tip.title}</div>
                                <div className="tip-card-description">{tip.description}</div>
                                <a
                                    href={tip.linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="tip-card-link"
                                >
                                    {tip.linkText}
                                </a>
                            </div>
                        ))}
                    </div>
                    <div className="tip-carousel-nav">
                        <button
                            className="tip-carousel-btn"
                            onClick={handlePrevPage}
                            aria-label="Previous tips"
                        >
                            <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
                        </button>
                        <div className="tip-carousel-dots">
                            {Array.from({ length: totalPages }).map((_, index) => (
                                <button
                                    key={index}
                                    className={`tip-carousel-dot ${index === currentPage ? 'active' : ''}`}
                                    onClick={() => setCurrentPage(index)}
                                    aria-label={`Go to page ${index + 1}`}
                                />
                            ))}
                        </div>
                        <button
                            className="tip-carousel-btn"
                            onClick={handleNextPage}
                            aria-label="Next tips"
                        >
                            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
            </>
        );
    };

    const getMainContent = () => {
        return (
            <>
                <ApiErrorMessages error={organization.error} />
                <ApiErrorMessages error={me.error} />
                <ApiErrorMessages error={apiError} />

                {/* Search Section */}
                <div className="landing-search-section">
                    <h2 className="landing-search-title">Participant Search</h2>
                    {!me.error && !organization.error && <ParticipantSearch>
                        <SearchResultsTable />
                    </ParticipantSearch>}
                </div>
            </>
        );
    };

    return (
        <section id="main-content" tabIndex={-1}>
            <div className="section">
                <Protect user={me.user}
                    roles={['Admin', 'Contributor']}
                    behavior="hide">
                    <SideMenu
                        id="landing-side-menu"
                        title="Quick Links"
                        options={sideMenuOptions}
                        headerContent={getHeaderContent()}
                        mainContent={getMainContent()}
                        ariaLabel="Landing Page Actions Menu"
                    />
                </Protect>
                <Protect user={me.user}
                    roles={['Viewer']}
                    behavior="hide">
                    {/* Viewers don't see the side menu, just the content */}
                    <div className="row justify-content-center">
                        <div className="column-medium-10">
                            {getHeaderContent()}
                            {getMainContent()}
                        </div>
                    </div>
                </Protect>
            </div>
            <Modal id="public-forms-paywall" 
                visible={showPublicFormsPaywall} 
                className="small" 
                title="Paid Feature" 
                onClose={() => setShowPublicFormsPaywall(false)}>
                <div className="row">
                    <div className="column">
                        <p>
                            <strong>Public Submissions</strong> is a paid feature that allows you to create public intake forms 
                            and collect submissions from anyone with the link.
                        </p>
                        <p>
                            Upgrade to a paid plan to enable Public Submissions.
                        </p>
                    </div>
                </div>
                <div className="row margin-top-3 justify-content-between">
                    <div className="column-auto">
                        <Button id="close-paywall" 
                            text="Close" 
                            className="secondary" 
                            onClick={() => setShowPublicFormsPaywall(false)} />
                    </div>
                    {me.user?.userRoleName === 'Admin' && <div className="column-auto">
                        <Button id="upgrade-plan" 
                            text="View Plans" 
                            className="action" 
                            href="/admin/billing" />
                    </div>}
                </div>
            </Modal>
        </section>
    )
}

export default LandingPageV2;
